import {
  AtLeastOne,
  Ethnicities,
  Gender,
  GeoCoord,
  MeDTO,
  Nationalities,
  OnBoardingBits,
  PhoneCountriesDTO,
  Prompt,
  Settings,
  UserDetailedDTO,
  UserDetailedMoreAboutMeFields,
  UserSharedDTO,
  VisitSource,
} from '@/domain';
import {
  httpDelete,
  httpGet,
  httpPatch,
  httpPost,
  httpPut,
} from '@/shared/api/serve';

export async function passedOnboarding(key: OnBoardingBits) {
  return httpPost<void>(`/onboardings?bit=${key}`);
}

export async function fetchUserDetailed(
  {nickname, source}: {nickname: string; source: VisitSource},
  params: Parameters<typeof httpGet>[1] = {}
) {
  const {data} = await httpGet<UserDetailedDTO>(
    `/users?nickname=${nickname}&source=${source}&include_sent_reaction=1&include_match=1`,
    params
  );

  return data.result;
}

export function trackVisit(payload: {nickname: string; source: VisitSource}) {
  return httpPost<void>('/visits', payload);
}

export async function shareProfile(payload: {
  avatar_id: number;
  nickname: string;
}) {
  const {data} = await httpPost<{link: string}>('/users/share', {
    ...payload,
    type: 'tell_friend',
  });

  return data.result;
}

export async function fetchSharedLinkDetails(sharedToken: string) {
  const {data} = await httpGet<UserSharedDTO>('users/share', {
    params: {
      token: sharedToken,
    },
  });

  return data.result;
}

export type ChangeMeFields = Settings & {
  about: string;
  dob: string;
  ethnicities: Ethnicities[];
  first_name: string;
  gender: Gender;
  goal: string;
  interests: number[];
  languages: number[];
  nationalities: Nationalities[];
  prompts: Prompt[];
  school: string;
  school_info: string;
  tr_lang: string;
  work: string;
  work_info: string;
  // used when user change app language
  lang: string;
} & UserDetailedMoreAboutMeFields & {onboarding_completed: true};

export async function changeMe(fields: AtLeastOne<ChangeMeFields>) {
  const {data} = await httpPut<MeDTO>('users', fields);

  return data.result.me;
}

export async function uploadAvatar({
  file,
  index,
  fileName,
  lastModify,
}: {
  file: Blob | ArrayBuffer;
  index: number;
  fileName?: string;
  lastModify?: number;
}) {
  const {data} = await httpPost<MeDTO>('users/avatar', file, {
    params: {
      index,
      file_name: fileName,
      lma: lastModify,
    },
    headers: {
      'Content-Type': 'image/*',
    },
    timeout: 30000,
  });

  return data.result.me;
}

export async function switchAvatars({
  firstId,
  secondId,
}: {
  firstId: number;
  secondId: number;
}) {
  const {data} = await httpPatch<MeDTO>(
    `users/avatar?one=${firstId}&two=${secondId}`
  );
  return data.result.me;
}

export async function sendConfirmationEmail() {
  await httpPost('email_confirmation');
}

export async function confirmEmail({
  email,
  code,
}: {
  email: string;
  code: string;
}) {
  const params = new URLSearchParams({
    email,
    code,
  });
  await httpPost(`email_confirmation?${params}`);
}

export async function saveUserLocation(coords: GeoCoord) {
  const {data} = await httpPost<MeDTO>('/users/city', coords, {
    params: {
      verified_location: '1',
    },
  });
  return data.result.me;
}

export async function uploadSignUpAvatar({
  imageBinary,
  fileName,
  lastModify,
}: {
  imageBinary: string | ArrayBuffer;
  fileName?: string;
  lastModify?: number;
}) {
  const queryParams = new URLSearchParams();
  if (fileName) {
    queryParams.append('file_name', fileName);
  }

  if (lastModify) {
    queryParams.append('lma', String(lastModify));
  }

  const {data} = await httpPost<{token: string}>(
    `sign/up/avatar?${queryParams}`,
    imageBinary,
    {
      headers: {
        'Content-Type': 'image/*',
      },
      timeout: 30000,
    }
  );

  return data.result;
}

export async function deleteAvatar(id: number) {
  const {data} = await httpDelete<MeDTO>(`users/avatar?id=${id}`);
  return data.result.me;
}

export async function fetchPhoneCountries() {
  const params = new URLSearchParams({
    os: 'web',
    lang: 'en',
  });

  const {data} = await httpGet<PhoneCountriesDTO>(
    `/sign/in/phone/countries?${params}`
  );
  return data.result.list;
}
